import type { Enum } from '../types/enum';

export const compareByName = (
    a: { name?: string; value?: string },
    b: { name?: string; value?: string }
) =>
    a.name && b.name
        ? a.name.localeCompare(b.name, 'en', { numeric: true })
        : a.value && b.value
          ? a.value.localeCompare(b.value, 'en', { numeric: true })
          : 0;

export function compareByField<T extends object, Key extends keyof T>(
    fieldName: Key,
    a: T,
    b: T
): number {
    if (typeof a[fieldName] === 'string' && typeof b[fieldName] === 'string') {
        return (a[fieldName] as unknown as string).localeCompare(
            b[fieldName] as unknown as string,
            'en',
            { numeric: true }
        );
    } else if (
        typeof a[fieldName] === 'number' &&
        typeof b[fieldName] === 'number'
    ) {
        const valueA = a[fieldName] as unknown as number;
        const valueB = b[fieldName] as unknown as number;
        if (valueA < valueB) {
            return 1;
        } else if (valueA > valueB) {
            return -1;
        } else {
            return 0;
        }
    }
    return 0;
}

export const elapsedTimeDisplayResolver = (elapsedTime: number) => {
    const timeInSeconds = elapsedTime / 1000;
    if (timeInSeconds < 0) {
        return `${elapsedTime}ms`;
    } else if (timeInSeconds > 0 && timeInSeconds < 60) {
        return `${Math.floor(timeInSeconds)}s ${elapsedTime % 1000}ms`;
    } else if (timeInSeconds > 60) {
        const timeInMinutes = timeInSeconds / 60;
        const seconds = Math.floor(timeInSeconds % 60);
        return `${Math.floor(timeInMinutes)}m ${seconds}s`;
    }
    return '';
};

export const formatIdentifier = (identifier: string | undefined): string => {
    if (!identifier) {
        return '';
    }
    return (
        identifier.charAt(0).toUpperCase() +
        identifier.slice(1).replaceAll('_', ' ').toLowerCase()
    );
};

export const formatToPascalCase = (identifier: string): string =>
    identifier.split('_').map(formatIdentifier).join(' ');

export const assertUnreachable = (x: never): never => {
    throw new Error(`Unhandled type ${x}`);
};

export const isNumber = (value: unknown): value is number =>
    (value || value === 0) && !Number.isNaN(Number(value));

export const isString = (value: unknown): value is string =>
    typeof value === 'string';

// biome-ignore lint/complexity/noBannedTypes: does not apply
export const isFunction = (value: unknown): value is Function =>
    typeof value === 'function';

export const isObject = (value: unknown): value is object =>
    value instanceof Object && !Array.isArray(value);

export const uniq = <T,>(array: Array<T>) => [...new Set(array)];

const compareAlphanumerically = (a: string, b: string): number =>
    a.localeCompare(b, undefined, { numeric: true });

export const sortBy = <T,>(
    cb: (item: T) => string
): ((a: T, b: T) => number) => {
    return (a, b) => compareAlphanumerically(cb(a), cb(b));
};

export const filterByString = (
    value: string,
    searchString: string,
    ignoreIfBlank?: boolean
) => {
    if (ignoreIfBlank && searchString === '') {
        return true;
    }
    return value.toLowerCase().includes(searchString.toLowerCase());
};

export const filterArrayByString = <T,>(
    searchString: string,
    arr: Array<T>,
    cb: (item: NoInfer<T>) => string
) => arr.filter((it) => filterByString(cb(it), searchString));

export const enumValues = <E extends string>(enumType: Enum<E>) =>
    Object.values(enumType) as Array<E>;
